// ---------------------------------------------------------------------------
// API client. Every call attaches Authorization: Bearer <token>.
// On 401 / invalid token it clears the session and redirects to /login.
// ---------------------------------------------------------------------------

const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  "https://testing-sanjeevika.djfoundation.co/api/config";

const TOKEN_KEY = "sanjeevika_admin_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Called by AuthContext to handle forced logout on auth failure.
let onAuthFailure = null;
export function setAuthFailureHandler(fn) {
  onAuthFailure = fn;
}

function buildUrl(path, params) {
  const url = new URL(`${BASE_URL}/${path.replace(/^\//, "")}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    });
  }
  return url.toString();
}

async function request(path, { method = "GET", params, body, isForm } = {}) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let payload;
  if (isForm) {
    payload = body; // FormData; browser sets multipart boundary
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(buildUrl(path, params), { method, headers, body: payload });
  } catch (networkErr) {
    throw new ApiError("Something went wrong. Please try again.", 0, networkErr);
  }

  if (res.status === 401) {
    // Do not let an older request clear a session that was established after
    // that request started.
    if (token && token === getToken() && onAuthFailure) onAuthFailure();
    throw new ApiError("Something went wrong. Please try again.", res.status);
  }

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    // API returned non-JSON (PHP warning/HTML). Treat as generic failure.
    throw new ApiError("Something went wrong.", res.status, text);
  }

  // API convention: { status: "success" | "error", message, data/... }
  if (data && data.status === "error") {
    throw new ApiError(data.message || "Something went wrong.", res.status, data);
  }

  return data;
}

export class ApiError extends Error {
  constructor(message, status, detail) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export const api = {
  get: (path, params) => request(path, { method: "GET", params }),
  post: (path, body) => request(path, { method: "POST", body }),
  put: (path, body) => request(path, { method: "PUT", body }),
  del: (path, body) => request(path, { method: "DELETE", body }),
  postForm: (path, formData) => request(path, { method: "POST", body: formData, isForm: true }),
};

export { BASE_URL };
